import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, StickyNote, History } from "lucide-react";
import { api } from "../../lib/api";

const KIND_META = {
  booking: { color: "border-sky-300 bg-sky-50 text-sky-900", icon: CalendarDays },
  note: { color: "border-slate-300 bg-slate-50 text-slate-900", icon: StickyNote },
  audit: { color: "border-slate-200 bg-white text-slate-700", icon: History },
};

export function CustomerActivityTab({ email }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/customers/${encodeURIComponent(email)}/timeline?limit=300`)
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], count: 0 }));
  }, [email]);

  if (data === null) return <div className="text-sm text-slate-500 p-6">Loading…</div>;

  const items = data.items || [];
  if (items.length === 0) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="cp-activity-empty">No activity recorded for this customer yet.</div>;
  }

  const byDay = {};
  for (const e of items) {
    const day = (e.at || "").slice(0, 10) || "-";
    (byDay[day] ||= []).push(e);
  }
  const days = Object.keys(byDay).sort().reverse();

  return (
    <div className="space-y-4" data-testid="cp-activity">
      {days.map((day) => (
        <div key={day} className="rounded-xl border border-slate-200 bg-white p-6" data-testid={`cp-activity-day-${day}`}>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            {day === "-" ? "Earlier" : new Date(day).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
          </div>
          <ol className="mt-3 space-y-3">
            {byDay[day].map((e, idx) => {
              const meta = KIND_META[e.kind] || KIND_META.audit;
              const Icon = meta.icon;
              const time = e.at ? new Date(e.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "";
              const content = (
                <div className={`rounded-lg border p-3 ${meta.color}`}>
                  <div className="flex items-start gap-2">
                    <Icon className="w-4 h-4 mt-0.5 opacity-80 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-medium capitalize text-sm">{e.title}</div>
                        <span className="text-[11px] opacity-70 whitespace-nowrap">{time}</span>
                      </div>
                      {e.description && <div className="text-xs opacity-80 mt-0.5 whitespace-pre-line line-clamp-3">{e.description}</div>}
                    </div>
                  </div>
                </div>
              );
              return (
                <li key={`${e.kind}-${idx}`} data-testid={`cp-activity-row-${e.kind}`}>
                  {e.link ? <Link to={e.link} className="block hover:opacity-90">{content}</Link> : content}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
